/**
 * Lot & Elevation DOM Component
 *
 * Renders the Lot & Elevation content panel.
 * Placeholder content — to be updated with actual lot and elevation details.
 *
 * @module nativeInteractiveSiteMap/components/lotAndElevation.dom
 */

import fastdom from 'fastdom';
import { backButtonIconSvg, informationIconSvg } from 'constants/svg-assets';
import getLotGroupCardHtml from './lotGroupCard.dom';

/**
 * Creates the Lot & Elevation content component
 * @param {Object} floorPlan - The currently selected floor plan
 * @param {Object} lot - The currently selected lot
 * @returns {{ getElement: Function, remove: Function }}
 */
export default function createLotAndElevationDom(floorPlan = {}, lot = {}) {
  const state = { root: null };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.className = [
      'nhs-c-native-interactive-site-map__content',
      'nhs-mq-native-interactive-site-map__content-medium',
      'nhs-mq-native-interactive-site-map__content-large',
    ].join(' ');
    
    // Defensive: Handle null lot gracefully
    if (!lot) {
      return;
    }

    // Build size string with formattedSquareFeet from floor plan
    const size = lot.lotSize ? `${lot.lotSize} Sq Ft` : null;
    const imageSrc = floorPlan?.image || null; // Use actual image URL if available
    const name = floorPlan?.name || null; // Use actual name if available
    const lotNumber = lot?.displayLabel ? `Lot ${lot.displayLabel}` : null;
    const group = lot?.lotGroupName  ? `- Group ${lot.lotGroupName}` :  null;
    const type = lot?.lotType || null;
    const facing = lot?.lotFacing || null;
    
    state.root.innerHTML = `
      <div class="nhs-u-padding--0-500 nhs-u-flex-column nhs-u-gap--500">
        <!-- Placeholder image for Lot & Elevation -->
        <img class="nhs-c-lotelevation__image nhs-mq-lotelevation__image-large" 
          src="${imageSrc}" 
          alt="${name}">

        <!-- Header with title and "Change Lot" button -->
        <div class="nhs-u-display--flex nhs-u-justify-content-space-between nhs-u-items-center nhs-u-padding--200-0">
          
          <h2 class="nhs-u-display--flex nhs-u-items-center nhs-u-gap--100">
            <span class="nhs-o-typography--sans-body-1-small-regular">
              Selected Lot
            </span>
            <span class="nhs-c-lotelevation__tooltip nhs-mq-lotelevation__tooltip-large nhs-u-color--gray-700 nhs-u-cursor--pointer nhs-u-hide-mobile">
              ${informationIconSvg}
              <span class="nhs-c-lotelevation__tooltip-text">
                Select a lot using the Map
              </span>
            </span>
          </h2>

          <div class="nhs-c-lotelevation__change-lot nhs-mq-lotelevation__change-lot-large nhs-u-cursor--pointer nhs-u-color--teal-1000 nhs-u-gap--100 nhs-o-typography--sans-label-1-extra-extra-small-regular">
            <span>Change Lot</span>
            ${backButtonIconSvg}
          </div>
        </div>

        <!-- Lot Details Card -->
        ${getLotGroupCardHtml({ lotNumber, group, size, type, facing })}

        <div class="nhs-u-border-top--1 nhs-u-border-color--neutral-300 nhs-u-padding-top--100"></div>
      </div>
    `;
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    remove() {
      fastdom.mutate(() => {
        state.root?.remove();
        state.root = null;
      });
    },
  };
}